import React from 'react';

import { Button as MuiButton, CircularProgress, Tooltip } from '@mui/material';

import { DEEP_BLUE, SCOUT_BLUE, SCOUT_TEAL, WHITE } from '../../ThemeContext/constants';

import type { ButtonProps as MuiButtonProps, SxProps } from '@mui/material';

export interface ButtonProps extends MuiButtonProps {
  id?: string;
  buttonVariant?: 'primary' | 'secondary' | 'text' | 'outline';
  buttonColor?: 'base' | 'red' | 'pink' | 'purple';
  iconPosition?: 'no' | 'leading' | 'trailing' | 'only';
  minWidth?: string;
  icon?: React.ReactNode;
  tooltip?: string;
}

const sizeStyles = {
  small: {
    no: { padding: '8px 16px' },
    leading: { padding: '8px 16px 8px 8px' },
    trailing: { padding: '8px 8px 8px 16px' },
    only: { padding: '8px' },
    fontSize: '12px',
    lineHeight: '16px',
    height: '36px',
    iconGap: '8px',
    iconSize: 16
  },
  medium: {
    no: { padding: '8px 16px' },
    leading: { padding: '8px 16px 8px 12px' },
    trailing: { padding: '8px 12px 8px 16px' },
    only: { padding: '8px' },
    fontSize: '14px',
    lineHeight: '18px',
    height: '40px',
    iconGap: '8px',
    iconSize: 20
  },
  large: {
    no: { padding: '16px 24px' },
    leading: { padding: '16px 24px 16px 16px' },
    trailing: { padding: '16px 16px 16px 24px' },
    only: { padding: '16px' },
    fontSize: '16px',
    lineHeight: '20px',
    height: '64px',
    iconGap: '8px',
    iconSize: 24
  }
};

const baseColors = {
  primary: { bg: SCOUT_TEAL, text: WHITE, hover: '#4AA9A5', border: 'transparent' },
  secondary: { bg: '#DEF3F2', text: SCOUT_BLUE, hover: '#CCEDEB', border: 'transparent' },
  text: {
    bg: 'transparent',
    text: '#418B88',
    hover: 'rgba(91, 196, 191, 0.1)',
    border: 'transparent'
  },
  outline: {
    bg: 'transparent',
    text: '#418B88',
    hover: 'rgba(91, 196, 191, 0.1)',
    border: '#418B88'
  }
};

const redButtonColors = {
  primary: { bg: '#cf726f', text: WHITE, hover: '#a15957', border: 'transparent' },
  secondary: { bg: '#FFF5F5', text: '#a15957', hover: '#FFE5E5', border: 'transparent' },
  text: {
    bg: 'transparent',
    text: '#A15957',
    hover: '#FCF2F2',
    border: 'transparent'
  },
  outline: {
    bg: 'transparent',
    text: '#A15957',
    hover: '#FCF2F2',
    border: '#a15957'
  }
};

const pinkButtonColors = {
  primary: { bg: '#BD20CB', text: WHITE, hover: '#AA1DB6', border: 'transparent' },
  secondary: { bg: '#FDECFF', text: '#BD20CB', hover: '#BD20CB3D', border: 'transparent' },
  text: {
    bg: 'transparent',
    text: '#BD20CB',
    hover: '#FCF2F2',
    border: 'transparent'
  },
  outline: {
    bg: 'transparent',
    text: '#BD20CB',
    hover: '#FCF2F2',
    border: '#BD20CB'
  }
};

const purpleButtonColors = {
  primary: { bg: DEEP_BLUE, text: WHITE, hover: '#28327E', border: 'transparent' },
  secondary: { bg: '#AFBCFF30', text: DEEP_BLUE, hover: '#AFBCFF55', border: 'transparent' },
  text: {
    bg: 'transparent',
    text: DEEP_BLUE,
    hover: '#AFBCFF30',
    border: 'transparent'
  },
  outline: {
    bg: 'transparent',
    text: DEEP_BLUE,
    hover: '#AFBCFF30',
    border: DEEP_BLUE
  }
};

type ButtonColorKey = NonNullable<ButtonProps['buttonColor']>;
type ButtonVariantKey = NonNullable<ButtonProps['buttonVariant']>;
type SizeKey = NonNullable<ButtonProps['size']>;

function resolveButtonColorPalette(buttonColor: ButtonColorKey) {
  switch (buttonColor) {
    case 'red':
      return redButtonColors;
    case 'pink':
      return pinkButtonColors;
    case 'purple':
      return purpleButtonColors;
    default:
      return baseColors;
  }
}

function resolveButtonVariantColors(buttonColor: ButtonColorKey, buttonVariant: ButtonVariantKey) {
  const palette = resolveButtonColorPalette(buttonColor);
  switch (buttonVariant) {
    case 'primary':
      return palette.primary;
    case 'secondary':
      return palette.secondary;
    case 'text':
      return palette.text;
    case 'outline':
      return palette.outline;
    default:
      return palette.primary;
  }
}

function resolveSizeConfig(size: SizeKey | undefined) {
  switch (size ?? 'medium') {
    case 'small':
      return sizeStyles.small;
    case 'large':
      return sizeStyles.large;
    default:
      return sizeStyles.medium;
  }
}

const renderSizedIcon = (icon: React.ReactNode, iconSize: number): React.ReactNode => {
  if (!icon) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        flexShrink: 0
      }}
    >
      {React.isValidElement(icon)
        ? React.cloneElement(icon as React.ReactElement<{ style?: React.CSSProperties }>, {
            style: {
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              fontSize: `${iconSize}px`,
              ...((icon.props as { style?: React.CSSProperties }).style ?? {})
            }
          })
        : icon}
    </span>
  );
};

// Design-system minimum so short labels ("OK", "Save") don't make tiny buttons.
// Icon-only buttons stay square; a caller's own minWidth still wins.
const DEFAULT_MIN_WIDTH = '72px';

const SystemDesignButton = ({
  id,
  children: label,
  size = 'medium',
  buttonVariant = 'primary',
  buttonColor = 'base',
  iconPosition: iconPositionProp,
  minWidth,
  fullWidth = false,
  icon,
  disabled = false,
  loading = false,
  onClick,
  tooltip,
  sx,
  ...rest
}: ButtonProps): React.ReactElement => {
  const hasMuiIconProp = Boolean(rest.startIcon) || Boolean(rest.endIcon);
  const iconPosition = !icon ? 'no' : (iconPositionProp ?? (!label ? 'only' : 'leading'));
  const colors = resolveButtonVariantColors(buttonColor, buttonVariant);
  const sizeConfig = resolveSizeConfig(size);
  const paddingConfig = (() => {
    switch (iconPosition) {
      case 'leading':
        return sizeConfig.leading;
      case 'trailing':
        return sizeConfig.trailing;
      case 'only':
        return sizeConfig.only;
      default:
        return sizeConfig.no;
    }
  })();
  const isIconOnly = iconPosition === 'only';

  const buttonStyles = {
    padding: paddingConfig.padding,
    fontSize: sizeConfig.fontSize,
    lineHeight: sizeConfig.lineHeight,
    minHeight: sizeConfig.height,
    height: isIconOnly ? sizeConfig.height : 'auto',
    minWidth: minWidth ?? (isIconOnly ? sizeConfig.height : DEFAULT_MIN_WIDTH),
    width: fullWidth ? '100%' : 'auto',
    backgroundColor: colors.bg,
    color: colors.text,
    border: colors.border !== 'transparent' ? `1px solid ${colors.border}` : 'none',
    borderRadius: isIconOnly ? '8px' : '6px',
    textTransform: 'none' as const,
    fontWeight: 'bold',
    fontFamily: '"Source Sans Pro", sans-serif',
    gap: hasMuiIconProp ? 0 : sizeConfig.iconGap,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& > svg, & > .MuiSvgIcon-root, & > span > svg, & > span > .MuiSvgIcon-root': {
      width: `${sizeConfig.iconSize}px`,
      height: `${sizeConfig.iconSize}px`,
      fontSize: `${sizeConfig.iconSize}px`,
      flexShrink: 0
    },
    '&:hover': { backgroundColor: colors.hover },
    '&:disabled': { backgroundColor: '#F0F0F0', color: '#858585' },
    '&.MuiButtonBase-root:focus-visible': {
      outline: `2px solid ${SCOUT_BLUE}`,
      outlineOffset: '2px',
      boxShadow: `0 0 0 2px ${WHITE}`,
      transition: 'none'
    }
  };

  const renderContent = (): React.ReactNode => {
    if (loading) {
      return <CircularProgress color="inherit" size={sizeConfig.iconSize} />;
    }

    const sizedIcon = renderSizedIcon(icon, sizeConfig.iconSize);

    if (iconPosition === 'only' && icon) {
      return sizedIcon;
    }

    if (iconPosition === 'leading' && icon) {
      return (
        <>
          {sizedIcon}
          <span>{label}</span>
        </>
      );
    }

    if (iconPosition === 'trailing' && icon) {
      return (
        <>
          <span>{label}</span>
          {sizedIcon}
        </>
      );
    }

    return label;
  };

  const svgFill = resolveButtonVariantColors(buttonColor, buttonVariant).text;
  const svgFillStyle = { svg: { g: { path: { fill: svgFill } } } };

  const button = (
    <MuiButton
      disabled={disabled || !!loading}
      id={id}
      onClick={onClick}
      sx={[buttonStyles, svgFillStyle, sx].flat() as SxProps}
      {...rest}
    >
      {renderContent()}
    </MuiButton>
  );

  if (isIconOnly && tooltip) {
    return (
      <Tooltip
        placement="bottom"
        title={tooltip}
        slotProps={{
          tooltip: {
            sx: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: '14px' }
          }
        }}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default SystemDesignButton;
